import { http } from 'config';
import type { FilterCollectionNoteType } from 'features/CollectionNotes';
import type { UptimeFiltersType } from 'features/Uptime';
import _ from 'lodash'; // ! lodash is used for snake_case conversion, cursed
import {
  type ChargePoint,
  type ChargepointUptimeDay,
  type CollectionNote,
  type CollectionNoteSummary,
  type ConnectorUptimeSummary,
  type ResetType,
  type TaskStatus,
} from 'types/swagger';

import {
  CHARGEPOINT_GROUP_BY_ID_URL,
  CHARGEPOINT_GROUPS_URL,
  CHARGEPOINTS_BY_ASSET_ID_URL,
  CHARGEPOINTS_BY_GRID_CONNECTION_ID_URL,
  CHARGEPOINTS_BY_ID_URL,
  CHARGEPOINTS_RESET_URL,
  CHARGEPOINTS_TRIGGER_MESSAGE_URL,
  CHARGEPOINTS_UPTIME_DAY_URL,
  CHARGEPOINTS_URL,
  COLLECTION_NOTES_SUMMARY_URL,
  COLLECTION_NOTES_URL,
  CONNECTORS_UPTIME_BETWEEN_URL,
  HEARTBEAT_BY_ID_URL,
  SUMMARY_URL,
  SUMMARY_XLSX_URL,
} from './endpoints';

export const getChargePointById = async (chargePointId: number) =>
  await http.get(CHARGEPOINTS_BY_ID_URL(chargePointId));

// ! Sin uso aparentemente
export const getChargePoints = async (pageSize = 999) =>
  await http.get(CHARGEPOINTS_URL, {
    params: { page_size: pageSize },
  });

// ! Duplicado 8 lineas más arriba
export const getChargePoint = async (chargePointId: number) =>
  await http.get(CHARGEPOINTS_BY_ID_URL(chargePointId));

// ! Sin uso aparentemente
export const getChargePointsByAssetId = async (assetId: number, pageSize = 999) =>
  await http.get(CHARGEPOINTS_BY_ASSET_ID_URL(assetId), {
    params: { page_size: pageSize },
  });

export const getChargePointsByGridConnectionId = async (gridConnectionId: number, pageSize = 999) =>
  await http.get(CHARGEPOINTS_BY_GRID_CONNECTION_ID_URL(gridConnectionId), {
    params: { page_size: pageSize },
  });

export const resetChargePoint = async (chargePointId: number, resetType: ResetType) =>
  await http.post(CHARGEPOINTS_RESET_URL(chargePointId), {
    type: resetType,
  });

export const triggerRefreshChargePoint = async (chargePointId: number) =>
  await http.post(CHARGEPOINTS_TRIGGER_MESSAGE_URL(chargePointId), {
    message_trigger: 'StatusNotification', // ! No debería estar hard coded así
  });

export const getChargePointHeartbeat = async (heartBeatId: number) =>
  await http.get(HEARTBEAT_BY_ID_URL(heartBeatId));

export const getChargePointGroups = async (pageSize = 999) =>
  await http.get(CHARGEPOINT_GROUPS_URL, {
    params: { page_size: pageSize },
  });

export const updateChargePointGroup = async (
  id: number,
  data: { name: string; charge_points?: number[] }
) => await http.patch(CHARGEPOINT_GROUP_BY_ID_URL(id), data);

export const removeChargePointGroup = async (id: number) =>
  await http.delete(CHARGEPOINT_GROUP_BY_ID_URL(id));

export const createChargePointGroup = async (name: string) =>
  await http.post(CHARGEPOINT_GROUPS_URL, { name });

export const updateChargePoint = async (chargePointId: number, data: { group: number }) =>
  await http.patch<ChargePoint>(CHARGEPOINTS_BY_ID_URL(chargePointId), data);

export const getConnectorsUptime = async (
  connectorId: number,
  startDate: string,
  endDate: string
) =>
  await http.get<ConnectorUptimeSummary>(
    CONNECTORS_UPTIME_BETWEEN_URL(connectorId, startDate, endDate)
  );

export const getChargePointUptimeDay = async (chargePointId: number) =>
  await http.get<ChargepointUptimeDay[]>(CHARGEPOINTS_UPTIME_DAY_URL(chargePointId));

export const getCollectionNotes = async (
  filters?: FilterCollectionNoteType,
  page = 1,
  pageSize = 999
) =>
  await http.get<{ results: CollectionNote[] }>(COLLECTION_NOTES_URL, {
    params: { page, page_size: pageSize, ...filters },
  });

export const getCollectionNotesSummary = async (
  filters?: FilterCollectionNoteType,
  pageSize = 999
) =>
  await http.get<CollectionNoteSummary>(COLLECTION_NOTES_SUMMARY_URL, {
    params: { ...filters, page_size: pageSize },
  });

// ! Sin uso, el endpoint no esta documentado en Swagger
export const requestDownloadCollectionNote = async (filters: FilterCollectionNoteType) =>
  await http.get<TaskStatus>(`/chargepoints/collection_notes/download/`, {
    params: filters,
  });

// ! Esto debería estar manejado por la query fn y params, no como una llamada a la API con lógica dentro
export const getChargepointsUptimeSummary = async (filters: UptimeFiltersType, pageSize = 999) => {
  let baseEndpoint = `${SUMMARY_URL}?page_size=${pageSize}`;

  Object.keys(filters)
    .filter((filter) => filter !== 'chargePointIds')
    .forEach((filterName) => {
      const value = filters[filterName as keyof UptimeFiltersType];
      if (value && value !== -1) {
        const formattedValue = Array.isArray(value) ? value.join(',') : value;
        baseEndpoint += `&${_.snakeCase(filterName)}=${formattedValue}`;
      }
    });

  const useAllChargepoints =
    filters.chargePointIds?.length === 0 ||
    (filters.chargePointIds?.length === 1 && filters.chargePointIds[0] === -1);

  if (filters.chargePointIds && !useAllChargepoints)
    baseEndpoint += `&${_.snakeCase('chargePointIds')}=${filters['chargePointIds'].join(',')}`;

  return await http.get(baseEndpoint);
};

// ! Lo mismo que el anterior, pero para el XLSX
export const requestDownloadChargepointsUptimeSummary = async (filters: UptimeFiltersType) => {
  let baseEndpoint = SUMMARY_XLSX_URL;
  let isFirstParam = true;

  Object.keys(filters)
    .filter((filter) => filter !== 'chargePointIds')
    .forEach((filterName) => {
      const value = filters[filterName as keyof UptimeFiltersType];
      if (value && value !== -1) {
        const formattedValue = Array.isArray(value) ? value.join(',') : value;
        baseEndpoint += `${isFirstParam ? '?' : '&'}${_.snakeCase(filterName)}=${formattedValue}`;
        isFirstParam = false;
      }
    });

  const useAllChargepoints =
    filters.chargePointIds?.length === 0 ||
    (filters.chargePointIds?.length === 1 && filters.chargePointIds[0] === -1);

  if (filters.chargePointIds && !useAllChargepoints) {
    baseEndpoint += `${isFirstParam ? '?' : '&'}${_.snakeCase('chargePointIds')}=${filters['chargePointIds'].join(',')}`;
  }

  return await http.get(baseEndpoint);
};
